import { useEffect, useState } from "react";

import { isNil } from "lodash";
import { usePostHog } from "posthog-js/react";
import { useSWRConfig } from "swr";

import { useWriterComponentState } from "hooks";

import { MarkBookedOffPlatformForm } from "./MarkBookedOffPlatformForm";
import { INITIAL_OFF_PLATFORM_BOOKING_STATE } from "./types";
import { ClientAppEvent } from "../../../../constants";
import {
  extractDateFromIsoString,
  getOffPlatformBookingPendingUrl,
  naiveDateToISOString,
  startLessOrEqualToEnd,
} from "../../../../utils";

import type {
  OffPlatformBookingFormState,
  OffPlatformBookingSource,
} from "./types";

interface Props {
  shipment: ShipperCondensedShipment;
  source: OffPlatformBookingSource;
  onReported: VoidFunction;
}

/*
Mounted only while the modal is open, so each opening starts from a clean form
without a reset effect. Only a submitted report is stored server side, so a
half-filled one is not worth carrying between openings.
*/
export function MarkBookedOffPlatformFormContainer(props: Props) {
  const { shipment, source, onReported } = props;
  const posthog = usePostHog();
  const { mutate } = useSWRConfig();

  const [formState, setFormState] = useState<OffPlatformBookingFormState>(
    INITIAL_OFF_PLATFORM_BOOKING_STATE,
  );
  const [validationError, setValidationError] = useState<UIError>(null);

  const isProject = shipment.info.type === "MULTI_SHIPMENT_PROJECT";

  useEffect(() => {
    posthog?.capture(ClientAppEvent.OFF_PLATFORM_BOOKING_MODAL_OPENED, {
      shipment_id: shipment.info.id,
      shipment_type: shipment.info.type,
      source,
    });
    // Fires once per opening, which is exactly this component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onValueChanged = <T,>(
    key: keyof OffPlatformBookingFormState,
    value: T,
  ): void => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const {
    loading,
    error: writeError,
    write,
  } = useWriterComponentState({
    errorMessage:
      "Failed to report the booking. Please contact us if the problem persists.",
    errorMessageKey: "message",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipment.info.id}/off-platform-bookings`,
      body: {
        carrier_email: formState.carrierEmail,
        carrier_company_name: formState.carrierCompanyName,
        equipment_type: formState.equipmentType,
        pickup_start:
          formState.pickupStart === ""
            ? ""
            : naiveDateToISOString(formState.pickupStart),
        pickup_start_time: null,
        delivery_eta:
          formState.deliveryEta === ""
            ? ""
            : naiveDateToISOString(formState.deliveryEta),
        delivery_eta_time: null,
        price: formState.price,
        currency: formState.currency,
        notes: formState.notes,
        tracking_link_offered: formState.trackingLinkOffered,
        quoting_unit_of_measure: shipment.info.quoting_unit_of_measure,
        // The shared project fields write the single "Loads Booked" value to both
        // bounds, so either one carries the count the endpoint wants.
        loads_booked: isProject ? formState.minimumLoads : null,
      },
    },
  });

  const validateForm = (): string | null => {
    if (
      formState.carrierEmail.trim() === "" &&
      formState.carrierCompanyName.trim() === ""
    ) {
      return "Enter the carrier's email or company name.";
    }

    if (isNil(formState.price) || formState.price <= 0) {
      return "Price must be greater than 0.";
    }

    if (formState.pickupStart === "" || formState.deliveryEta === "") {
      return "Enter both a pickup ETA and a delivery ETA.";
    }

    if (!startLessOrEqualToEnd(formState.pickupStart, formState.deliveryEta)) {
      return "Pickup start must be before or equal to the delivery ETA.";
    }

    if (formState.equipmentType === "") {
      return "Please select an equipment type.";
    }

    if (
      isProject &&
      (isNil(formState.minimumLoads) || formState.minimumLoads < 1)
    ) {
      return "Loads booked must be greater than 0.";
    }

    return null;
  };

  const onSubmit = () => {
    const error = validateForm();
    setValidationError(error);
    if (error != null) {
      return;
    }

    // Captured in the success callback rather than here: the backend emits
    // OFF_PLATFORM_BOOKING_REPORTED only once the report is stored, and the two
    // metrics have to agree.
    write(() => {
      posthog?.capture(ClientAppEvent.OFF_PLATFORM_BOOKING_SUBMITTED, {
        shipment_id: shipment.info.id,
        shipment_type: shipment.info.type,
        has_carrier_email: formState.carrierEmail.trim() !== "",
        has_carrier_company_name: formState.carrierCompanyName.trim() !== "",
        source,
      });
      // A report triggers no shipment cache revalidation, so the pending alert
      // would otherwise only appear after a reload.
      mutate(getOffPlatformBookingPendingUrl(shipment.info.id));
      onReported();
    });
  };

  const onPopulateShipmentRequirements = () => {
    onValueChanged(
      "pickupStart",
      extractDateFromIsoString(shipment.first_point.start_time),
    );
    onValueChanged(
      "deliveryEta",
      extractDateFromIsoString(shipment.last_point.start_time),
    );

    if (shipment.equipments.length > 0) {
      onValueChanged("equipmentType", shipment.equipments[0].type);
    }
  };

  return (
    <MarkBookedOffPlatformForm
      shipment={shipment}
      formState={formState}
      onValueChanged={onValueChanged}
      onSubmit={onSubmit}
      onPopulateShipmentRequirements={onPopulateShipmentRequirements}
      error={validationError ?? writeError}
      loading={loading}
    />
  );
}
